import { __decorate } from "tslib";
import { LitElement, html } from 'lit';
import { query, state } from 'lit/decorators.js';
import Cropper from 'cropperjs';
import '@shoelace-style/shoelace/dist/components/range/range.js';
import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import '../dialog';
import '../chip';
import '../button';
import '../icon';
import '../typography/tg-title';
import '../typography/tg-body-two';
import plusIcon from '../../assets/icons/plus';
import minusIcon from '../../assets/icons/minus';
import dragIcon from '../../assets/icons/drag';
import styles from './styles';
/**
 * @dependency amityeko-tg-dialog
 * @dependency amityeko-chip
 * @dependency amityeko-button
 * @dependency amityeko-icon
 * @dependency amityeko-tg-title
 * @dependency amityeko-tg-body-two
 *
 * @slot  upload- The file upload button
 *
 * @event amityeko-image-editor-confirm {{ imageData, fileName }} - Emitted when an image editor dialog is confirmed.
 * @event amityeko-image-editor-back {{ MouseEvent }} - Emitted when clicking an image editor dialog back button.
 * @event amityeko-image-editor-warning-confirm {{ MouseEvent }} - Emitted when an image size warning dialog is confirmed.
 * @event amityeko-image-editor-warning-cancel {{ MouseEvent }} - Emitted when clicking an image editor dialog cancel button.
 */
export default class AmityekoImageEditor extends LitElement {
    constructor() {
        super(...arguments);
        this.min = 1;
        this.max = 1.5;
        this.step = 0.01;
        this.isMinusBtnDisabled = true;
        this.isPlusBtnDisabled = false;
    }
    show(e) {
        var _a;
        //reset all cropper state
        (_a = this.cropper) === null || _a === void 0 ? void 0 : _a.destroy();
        if (e.target.files.length) {
            this.fileName = e.target.files[0].name;
            //create image url
            this.imageURL = URL.createObjectURL(e.target.files[0]);
            this.image.onload = () => {
                //set minimun width and height of the image
                if (this.image.naturalWidth >= 200 && this.image.naturalHeight >= 400) {
                    this.imageEditorDialog.show();
                    //crop image using cropperjs
                    this.cropper = new Cropper(this.image, {
                        viewMode: 1,
                        dragMode: 'move',
                        aspectRatio: 5 / 8,
                        minContainerWidth: 200,
                        minContainerHeight: 400,
                        minCropBoxWidth: 200,
                        minCropBoxHeight: 400,
                        background: false,
                        center: false,
                        zoomOnWheel: false,
                        zoomOnTouch: false,
                        cropBoxMovable: false,
                        cropBoxResizable: false,
                        guides: false,
                    });
                }
                else {
                    //show image size warning dialog
                    this.imageSizeWarningDailog.show();
                }
            };
        }
    }
    _setMinusAndPlusBtnStatus() {
        var _a, _b;
        this.isMinusBtnDisabled = ((_a = this.slRangeSlider) === null || _a === void 0 ? void 0 : _a.value) === this.min;
        this.isPlusBtnDisabled = ((_b = this.slRangeSlider) === null || _b === void 0 ? void 0 : _b.value) === this.max;
    }
    _onChangeSlider(e) {
        var _a;
        (_a = this.cropper) === null || _a === void 0 ? void 0 : _a.scale(this.slRangeSlider.value);
        this._setMinusAndPlusBtnStatus();
    }
    _handleSliderPlus() {
        var _a;
        if (this.slRangeSlider.value < this.max) {
            const newSlRangeValue = this.slRangeSlider.value + 0.1;
            if (newSlRangeValue < this.max) {
                this.slRangeSlider.value = newSlRangeValue;
            }
            else {
                this.slRangeSlider.value = this.max;
            }
            (_a = this.cropper) === null || _a === void 0 ? void 0 : _a.scale(this.slRangeSlider.value);
        }
        this._setMinusAndPlusBtnStatus();
    }
    _handleSliderMinus() {
        var _a;
        if (this.slRangeSlider.value > this.min) {
            const newSlRangeValue = this.slRangeSlider.value - 0.1;
            if (newSlRangeValue > this.min) {
                this.slRangeSlider.value = newSlRangeValue;
            }
            else {
                this.slRangeSlider.value = this.min;
            }
            (_a = this.cropper) === null || _a === void 0 ? void 0 : _a.scale(this.slRangeSlider.value);
        }
        this._setMinusAndPlusBtnStatus();
    }
    _onConfirmDone() {
        var _a;
        //crop the image and send the data
        const croppedImage = (_a = this.cropper) === null || _a === void 0 ? void 0 : _a.getCroppedCanvas();
        emit(this, 'amityeko-image-editor-confirm', {
            detail: {
                imageData: croppedImage,
                fileName: this.fileName,
            },
        });
        this.imageEditorDialog.hide();
    }
    _reset() {
        var _a;
        (_a = this.cropper) === null || _a === void 0 ? void 0 : _a.reset();
    }
    _handleImageEditorBack() {
        emit(this, 'amityeko-image-editor-back');
        this.imageEditorDialog.hide();
    }
    _handleImageSizeWarningConfirm() {
        emit(this, 'amityeko-image-editor-warning-confirm');
        this.imageSizeWarningDailog.hide();
    }
    _handleImageSizeWarningBack() {
        emit(this, 'amityeko-image-editor-warning-cancel');
        this.imageSizeWarningDailog.hide();
    }
    render() {
        return html `
      <!-- Import external css file from cropper js-->
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/cropperjs/1.5.12/cropper.min.css"
      />

      <label for="file">
        <slot name="upload"></slot>
        <input type="file" id="file" accept="image/*" @change=${this.show} />
      </label>
      <amityeko-dialog
        id="image_editor_dialog"
        .label=${'Upload Background'}
        .cancelButtonText=${'Cancel'}
        .confirmButtonText=${'Done'}
        ?hasBackButton=${true}
        @amityeko-back=${this._handleImageEditorBack}
        @amityeko-after-hide=${this._reset}
        @amityeko-confirm=${this._onConfirmDone}
      >
        <div class="content">
          <amityeko-chip class="drag__chip" selectionColor="#424242">
            <amityeko-icon slot="prefix" .iconSize=${'12px'} .color=${'#ffffff'}
              >${dragIcon}</amityeko-icon
            >
            Drag
          </amityeko-chip>
          <!-- The image container -->
          <div id="image_container" class="image__container">
            <img id="image" src=${this.imageURL || ''} />
          </div>
          <div class="range__wrapper">
            <amityeko-button
              .disabled=${this.isMinusBtnDisabled}
              @click=${this._handleSliderMinus}
              variant="ghost"
              circle
            >
              <amityeko-icon .iconSize=${'12px'}>${minusIcon}</amityeko-icon>
            </amityeko-button>
            <sl-range
              id="sl_range_slider"
              .min=${this.min}
              .max=${this.max}
              .step=${this.step}
              .tooltip=${'none'}
              @sl-change=${this._onChangeSlider}
            ></sl-range>
            <amityeko-button
              .disabled=${this.isPlusBtnDisabled}
              @click=${this._handleSliderPlus}
              variant="ghost"
              circle
            >
              <amityeko-icon .iconSize=${'12px'}>${plusIcon}</amityeko-icon>
            </amityeko-button>
          </div>
        </div>
      </amityeko-dialog>

      <!--Image Size Warning Dialog -->
      <amityeko-dialog
        id="image_size_warning_dialog"
        .variant=${'danger'}
        ?hasIconAsLabel=${true}
        .cancelButtonText=${'Cancel'}
        .confirmButtonText=${'Okay'}
        @amityeko-confirm=${this._handleImageSizeWarningConfirm}
        @amityeko-cancel=${this._handleImageSizeWarningBack}
      >
        <div class="warning__dialog__content">
          <amityeko-tg-title>Image is too small.</amityeko-tg-title>
          <amityeko-tg-body-two
            >We suggest using minimum 200px width and 400px height for background
            image.</amityeko-tg-body-two
          >
        </div>
      </amityeko-dialog>
    `;
    }
}
AmityekoImageEditor.styles = styles;
__decorate([
    query('#image_editor_dialog')
], AmityekoImageEditor.prototype, "imageEditorDialog", void 0);
__decorate([
    query('#image_size_warning_dialog')
], AmityekoImageEditor.prototype, "imageSizeWarningDailog", void 0);
__decorate([
    query('#image_container')
], AmityekoImageEditor.prototype, "imageContainer", void 0);
__decorate([
    query('#image')
], AmityekoImageEditor.prototype, "image", void 0);
__decorate([
    query('#sl_range_slider')
], AmityekoImageEditor.prototype, "slRangeSlider", void 0);
__decorate([
    state()
], AmityekoImageEditor.prototype, "cropper", void 0);
__decorate([
    state()
], AmityekoImageEditor.prototype, "min", void 0);
__decorate([
    state()
], AmityekoImageEditor.prototype, "max", void 0);
__decorate([
    state()
], AmityekoImageEditor.prototype, "step", void 0);
__decorate([
    state()
], AmityekoImageEditor.prototype, "imageURL", void 0);
__decorate([
    state()
], AmityekoImageEditor.prototype, "isMinusBtnDisabled", void 0);
__decorate([
    state()
], AmityekoImageEditor.prototype, "isPlusBtnDisabled", void 0);
__decorate([
    state()
], AmityekoImageEditor.prototype, "fileName", void 0);
if (!customElements.get('amityeko-image-editor')) {
    customElements.define('amityeko-image-editor', AmityekoImageEditor);
}
//# sourceMappingURL=index.js.map